import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, Save, Loader2, ArrowUpRight, Rocket, ExternalLink } from "lucide-react";
import SEO from "@/components/SEO";

const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY_ROW = { outlet: "", headline: "", url: "" };

/**
 * Admin-only Signal-Room Studio. Manage press mentions + trigger a corridor
 * launch fan-out email. Gated behind a static admin token (?token=...).
 */
export default function AdminSignalRoom() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token") || "";

  const [press, setPress] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | loading | saving | saved | error
  const [msg, setMsg] = useState("");
  const [launchCountry, setLaunchCountry] = useState("");
  const [launchStatus, setLaunchStatus] = useState("idle");
  const [launchResult, setLaunchResult] = useState(null);

  useEffect(() => {
    if (!token) return;
    setStatus("loading");
    fetch(`${API}/api/admin/signal-room/content?token=${encodeURIComponent(token)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => {
        setPress((d?.press?.length ? d.press : []).slice(0, 20));
        setStatus("idle");
      })
      .catch(() => setStatus("error"));
  }, [token]);

  const addRow = () => setPress((p) => [...p, { ...EMPTY_ROW }]);
  const rmRow = (i) => setPress((p) => p.filter((_, idx) => idx !== i));
  const updRow = (i, key, value) =>
    setPress((p) => p.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)));

  const save = async () => {
    setStatus("saving");
    setMsg("");
    try {
      const r = await fetch(
        `${API}/api/admin/signal-room/content?token=${encodeURIComponent(token)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ press: press.filter((r) => r.outlet && r.headline) }),
        }
      );
      if (!r.ok) throw new Error("save failed");
      const d = await r.json();
      setStatus("saved");
      setMsg(`Saved ${d.count} press mentions.`);
      setTimeout(() => setStatus("idle"), 2500);
    } catch (_) {
      setStatus("error");
      setMsg("Save failed. Check your token.");
    }
  };

  const launch = async () => {
    if (!launchCountry) return;
    setLaunchStatus("loading");
    setLaunchResult(null);
    try {
      const r = await fetch(
        `${API}/api/admin/launch-corridor?token=${encodeURIComponent(token)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ country: launchCountry.toUpperCase() }),
        }
      );
      if (!r.ok) throw new Error("launch failed");
      const d = await r.json();
      setLaunchResult(d);
      setLaunchStatus("done");
    } catch (_) {
      setLaunchStatus("error");
    }
  };

  if (!token) {
    return (
      <main className="min-h-[60vh] container-x py-20">
        <h1 className="font-display text-4xl text-forest">Signal-Room Studio</h1>
        <p className="mt-3 text-ink/70">
          Append <code>?token=YOUR_ADMIN_TOKEN</code> to this URL to open the editor.
        </p>
      </main>
    );
  }

  return (
    <main className="min-h-screen container-x py-14 md:py-20" data-testid="admin-signal-room">
      <SEO slug="admin-signal-room" title="Signal-Room Studio · Admin" description="" />
      <header className="mb-10 flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">Admin · Signal Room</div>
          <h1 className="mt-2 font-display font-medium text-forest text-4xl md:text-5xl tracking-tightest">
            Studio.
          </h1>
        </div>
        <a
          href={`${API}/api/press/signal-room-preview`}
          target="_blank"
          rel="noreferrer"
          className="btn-ghost text-sm"
          data-testid="signal-room-preview-link"
        >
          Preview digest <ExternalLink className="w-4 h-4" />
        </a>
      </header>

      {/* Press mentions editor */}
      <section className="mb-16 rounded-3xl border border-forest/15 bg-canvas p-6 md:p-8">
        <div className="flex items-baseline justify-between mb-5">
          <h2 className="font-display text-2xl text-forest">Press mentions</h2>
          <span className="text-xs text-ink/50">{press.length} / 20</span>
        </div>

        <div className="space-y-3">
          {press.map((row, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid grid-cols-12 gap-2 items-center"
              data-testid={`press-row-${i}`}
            >
              <input
                type="text"
                value={row.outlet}
                onChange={(e) => updRow(i, "outlet", e.target.value)}
                placeholder="Outlet · Financial Times"
                className="col-span-3 rounded-xl border border-forest/15 bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-forest"
                data-testid={`press-outlet-${i}`}
              />
              <input
                type="text"
                value={row.headline}
                onChange={(e) => updRow(i, "headline", e.target.value)}
                placeholder="Headline · Africa's remittance dam is cracking."
                className="col-span-5 rounded-xl border border-forest/15 bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-forest"
                data-testid={`press-headline-${i}`}
              />
              <input
                type="url"
                value={row.url}
                onChange={(e) => updRow(i, "url", e.target.value)}
                placeholder="https://…"
                className="col-span-3 rounded-xl border border-forest/15 bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-forest"
                data-testid={`press-url-${i}`}
              />
              <button
                type="button"
                onClick={() => rmRow(i)}
                className="col-span-1 h-10 rounded-xl border border-forest/15 grid place-items-center hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition-colors"
                aria-label="Remove"
                data-testid={`press-rm-${i}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </motion.div>
          ))}
        </div>

        <div className="mt-5 flex flex-wrap gap-3 items-center">
          <button
            type="button"
            onClick={addRow}
            disabled={press.length >= 20}
            className="btn-ghost text-sm"
            data-testid="press-add"
          >
            <Plus className="w-4 h-4" /> Add mention
          </button>
          <button
            type="button"
            onClick={save}
            disabled={status === "saving"}
            className="btn-accent text-sm"
            data-testid="press-save"
          >
            {status === "saving" ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Save className="w-4 h-4" /> Save
              </>
            )}
          </button>
          {msg && (
            <span className={"text-sm " + (status === "error" ? "text-red-600" : "text-forest/70")}>
              {msg}
            </span>
          )}
        </div>
      </section>

      {/* Corridor launch */}
      <section className="rounded-3xl bg-forest text-canvas p-6 md:p-8">
        <div className="flex items-baseline justify-between mb-5">
          <h2 className="font-display text-2xl text-canvas">Launch a corridor</h2>
          <span className="text-xs text-canvas/50">
            Sends the &ldquo;You spoke up&rdquo; email to every voter + waitlist match
          </span>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <input
            type="text"
            value={launchCountry}
            onChange={(e) => setLaunchCountry(e.target.value.toUpperCase().slice(0, 2))}
            placeholder="ISO code · CA"
            maxLength={2}
            className="w-32 rounded-xl bg-canvas/8 border border-canvas/20 px-4 py-2.5 text-sm font-mono focus:outline-none focus:border-lime uppercase"
            data-testid="launch-country"
          />
          <button
            type="button"
            onClick={launch}
            disabled={launchStatus === "loading" || !launchCountry}
            className="inline-flex items-center gap-1.5 bg-lime text-ink px-4 py-2.5 rounded-full text-sm font-semibold hover:bg-lime/90 transition-colors disabled:opacity-50"
            data-testid="launch-btn"
          >
            {launchStatus === "loading" ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Rocket className="w-4 h-4" /> Launch & notify
              </>
            )}
          </button>
          {launchResult && (
            <span
              className="text-sm text-lime font-mono"
              data-testid="launch-result"
            >
              {launchResult.country}: {launchResult.voters_notified} notified
              {launchResult.failed ? ` (${launchResult.failed} failed)` : ""}
            </span>
          )}
          {launchStatus === "error" && (
            <span className="text-sm text-red-300">Launch failed. Check the token.</span>
          )}
        </div>
      </section>
    </main>
  );
}
